import { useCallback, useEffect, useRef, useState } from 'react'
import { ProfileAvatar } from './index'
import styles from './HoverableAvatar.module.css'

interface PreviewPosition {
  top: number
  left: number
}

interface HoverableAvatarProps {
  profileImageUrl?: string
  userName?: string
  size?: number
  previewSize?: number
  hoverDelay?: number
  className?: string
}

const HoverableAvatar = ({
  profileImageUrl,
  userName,
  size = 28,
  previewSize = 180,
  hoverDelay = 250,
  className = ''
}: HoverableAvatarProps) => {
  const [showPreview, setShowPreview] = useState(false)
  const [previewPosition, setPreviewPosition] = useState<PreviewPosition>({
    top: 0,
    left: 0
  })
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const calculatePosition = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return null

    let left = rect.right + 10
    let top = rect.top

    if (left + previewSize > window.innerWidth) {
      left = rect.left - previewSize - 10
    }

    if (top + previewSize > window.innerHeight) {
      top = window.innerHeight - previewSize - 10
    }

    if (top < 10) {
      top = 10
    }

    return { top, left }
  }, [previewSize])

  const handleMouseEnter = useCallback(() => {
    if (!profileImageUrl) return

    timeoutRef.current = setTimeout(() => {
      const position = calculatePosition()
      if (!position) return

      setPreviewPosition(position)
      setShowPreview(true)
    }, hoverDelay)
  }, [profileImageUrl, hoverDelay, calculatePosition])

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setShowPreview(false)
  }, [])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!profileImageUrl) return

      e.stopPropagation()
      e.preventDefault()

      if (showPreview) {
        setShowPreview(false)
        return
      }

      const position = calculatePosition()
      if (!position) return

      setPreviewPosition(position)
      setShowPreview(true)
    },
    [profileImageUrl, showPreview, calculatePosition]
  )

  // Dismiss preview when clicking outside
  useEffect(() => {
    if (!showPreview) return

    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowPreview(false)
      }
    }

    document.addEventListener('click', handleOutsideClick)
    return () => document.removeEventListener('click', handleOutsideClick)
  }, [showPreview])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <div
        ref={containerRef}
        className={`${styles.container} ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <ProfileAvatar profileImageUrl={profileImageUrl} userName={userName} size={size} />
      </div>

      {showPreview && profileImageUrl && (
        <div
          className={styles.preview}
          style={{
            top: `${previewPosition.top}px`,
            left: `${previewPosition.left}px`,
            width: `${previewSize}px`,
            height: `${previewSize}px`
          }}
        >
          <img
            src={profileImageUrl}
            alt="Profile preview"
            className={styles.previewImage}
          />
        </div>
      )}
    </>
  )
}

export default HoverableAvatar
